// Code 128 bar/space patterns indexed by symbol value, written as binary
// digits: `1` is a bar module and `0` is a space module. Table from
// jsbarcode@3.11.6 (MIT).
const PATTERNS = [
  11011001100, 11001101100, 11001100110, 10010011000, 10010001100, 10001001100,
  10011001000, 10011000100, 10001100100, 11001001000, 11001000100, 11000100100,
  10110011100, 10011011100, 10011001110, 10111001100, 10011101100, 10011100110,
  11001110010, 11001011100, 11001001110, 11011100100, 11001110100, 11101101110,
  11101001100, 11100101100, 11100100110, 11101100100, 11100110100, 11100110010,
  11011011000, 11011000110, 11000110110, 10100011000, 10001011000, 10001000110,
  10110001000, 10001101000, 10001100010, 11010001000, 11000101000, 11000100010,
  10110111000, 10110001110, 10001101110, 10111011000, 10111000110, 10001110110,
  11101110110, 11010001110, 11000101110, 11011101000, 11011100010, 11011101110,
  11101011000, 11101000110, 11100010110, 11101101000, 11101100010, 11100011010,
  11101111010, 11001000010, 11110001010, 10100110000, 10100001100, 10010110000,
  10010000110, 10000101100, 10000100110, 10110010000, 10110000100, 10011010000,
  10011000010, 10000110100, 10000110010, 11000010010, 11001010000, 11110111010,
  11000010100, 10001111010, 10100111100, 10010111100, 10010011110, 10111100100,
  10011110100, 10011110010, 11110100100, 11110010100, 11110010010, 11011011110,
  11011110110, 11110110110, 10101111000, 10100011110, 10001011110, 10111101000,
  10111100010, 11110101000, 11110100010, 10111011110, 10111101110, 11101011110,
  11110101110, 11010000100, 11010010000, 11010011100, 1100011101011,
];

const START_B = 104;
const STOP = 106;
const CHECKSUM_MODULUS = 103;
const SET_B_FIRST_CHAR_CODE = 32;
const SET_B_LAST_CHAR_CODE = 127;

/** Modules of white space required on each side of a Code 128 symbol. */
export const CODE_128_QUIET_ZONE_MODULES = 10;

export const canEncodeCode128B = (value: string): boolean =>
  value.length > 0 &&
  Array.from(value).every(char => {
    const code = char.charCodeAt(0);
    return code >= SET_B_FIRST_CHAR_CODE && code <= SET_B_LAST_CHAR_CODE;
  });

/**
 * Encodes `value` as a Code 128 set B symbol (start, data, checksum, stop),
 * without quiet zones. Returns one character per module: `1` is a bar and `0`
 * is a space.
 */
export const encodeCode128B = (value: string): string => {
  if (!canEncodeCode128B(value)) {
    throw new RangeError(
      `Code 128 set B can't encode ${JSON.stringify(value)}`,
    );
  }
  const symbols = Array.from(
    value,
    char => char.charCodeAt(0) - SET_B_FIRST_CHAR_CODE,
  );
  const checksum =
    symbols.reduce(
      (sum, symbol, index) => sum + symbol * (index + 1),
      START_B,
    ) % CHECKSUM_MODULUS;
  return [START_B, ...symbols, checksum, STOP]
    .map(symbol => String(PATTERNS[symbol]))
    .join('');
};
